/**
 * displayName — canonical user/practitioner name resolver.
 *
 * Used across the app so the SAME person shows up under the SAME name
 * everywhere — profile page, comment author, post header, search
 * result, admin table, founder wall, share card, notification, etc.
 *
 * Priority (highest → lowest):
 *   1. `businessName` on the provider profile
 *      (a practitioner's "craft name" — what they want seekers to see)
 *   2. `user.name`
 *      (the legal/display name they entered on signup)
 *   3. `name` field if the object IS a user record
 *   4. The email handle (everything before `@`)
 *   5. "Cauldron Seeker" / "Cauldron Member" fallback
 *
 * Callers can pass any of:
 *   • a user record           → displayName(user)
 *   • a provider profile      → displayName(provider)
 *   • a provider with .user   → displayName(provider) (handles either)
 *   • a comment / post author → displayName({ userName, ... }) works too
 *
 * Never returns null/undefined. Always returns a non-empty string.
 */
export function displayName(input) {
  if (!input) return "Cauldron Member";
  const o = input;
  // Provider profile shape
  if (o.businessName && typeof o.businessName === "string" && o.businessName.trim()) {
    return o.businessName.trim();
  }
  // Nested user (some endpoints embed `user: {...}`)
  if (o.user && typeof o.user === "object") {
    const nested = displayName(o.user);
    if (nested && nested !== "Cauldron Member") return nested;
  }
  // User-shaped or comment-shaped
  for (const key of ["name", "displayName", "userName", "fullName"]) {
    const v = o[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  // Email handle
  if (typeof o.email === "string" && o.email.includes("@")) {
    return o.email.split("@")[0];
  }
  // Role-based fallback
  if (o.role === "PROVIDER") return "Cauldron Practitioner";
  if (o.role === "ADMIN") return "Cauldron Admin";
  return "Cauldron Member";
}

/**
 * displayInitial — first character of displayName(), uppercased.
 * Useful for avatar placeholders. Always returns a single character.
 */
export function displayInitial(input) {
  const n = displayName(input);
  return (n[0] || "C").toUpperCase();
}
